import api from './api';
import { Email, Attachment } from '../types/email';
import { API_BASE_URL } from '../utils/constants';
import { performOptimisticUpdate } from '../utils/optimisticUpdate';

// Re-export types for backward compatibility
export type { Email, Attachment };

export interface EmailThread {
  id: string;
  subject: string;
  participants: string[];
  message_count: number;
  last_message_at: string;
  is_read: boolean;
  is_archived: boolean;
  emails: Email[];
}

export interface EmailAccount {
  id: string;
  user_id: string;
  provider: string;
  email_address: string;
  oauth_token_expires_at: string | null;
  last_synced_at: string | null;
  sync_status: string;
  sync_error_message: string | null;
  is_active: boolean;
  account_status: string;
  auto_harmonize_enabled: boolean;
  signature: string | null;
  resync_status?: string;
  resync_progress?: {
    processed?: number;
    total?: number;
    new?: number;
    updated?: number;
    started_at?: string;
    completed_at?: string;
    error?: string;
    failed_at?: string;
  };
  nickname: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface EmailFolder {
  id: string;
  name: string;
  count: number;
  unread_count: number;
  level?: number;
  parent?: string | null;
  has_children?: boolean;
  is_virtual?: boolean;
}

export interface EmailLabel {
  id: string;
  name: string;
  type: 'system' | 'user';
}

export interface EmailFilters {
  is_read?: boolean;
  is_archived?: boolean;
  is_starred?: boolean;
  /** Betabox triage marker filter: marked states or 'none' for untriaged. */
  triage_state?: 'follow_up' | 'task' | 'none';
}

export interface EmailListResponse {
  emails: Email[];
  pagination: {
    page: number;
    per_page: number;
    has_next?: boolean;
    // Totals now come from GET /emails/counts (cached); the list endpoint
    // returns null to avoid a per-request COUNT over the whole mailbox.
    total_pages: number | null;
    total_items: number | null;
  };
}

export interface SendEmailRequest {
  email_account_id: string;
  to: string[];
  subject: string;
  body: string;
  cc?: string[];
  bcc?: string[];
  enable_tracking?: boolean;
}

export interface ReplyEmailRequest {
  body: string;
  reply_all?: boolean;
  to?: string[];
  cc?: string[];
  bcc?: string[];
}

export interface SyncResponse {
  message: string;
  synced_count: number;
}

export interface EmailCounts {
  total_inbox: number;
  total_unread: number;
}

// Email API methods
export const getEmailAccounts = async (): Promise<EmailAccount[]> => {
  const response = await api.get<EmailAccount[]>('/api/v1/email-accounts');
  return response.data;
};

export const getEmailAccountLabels = async (emailAccountId: string): Promise<EmailLabel[]> => {
  const response = await api.get<{ labels: EmailLabel[] }>(`/api/v1/email-accounts/${emailAccountId}/labels`);
  return response.data.labels;
};

export const syncEmails = async (): Promise<SyncResponse> => {
  const response = await api.post<SyncResponse>('/api/v1/emails/sync', {});
  return response.data;
};

export const getEmailCounts = async (emailAccountId?: string): Promise<EmailCounts> => {
  const params = emailAccountId ? `?email_account_id=${emailAccountId}` : '';
  const response = await api.get<EmailCounts>(`/api/v1/emails/counts${params}`);
  return response.data;
};

export const markEmailAsRead = async (emailId: string): Promise<void> => {
  await api.patch(`/api/v1/emails/${emailId}/mark-read`);
};

export const markEmailAsUnread = async (emailId: string): Promise<void> => {
  await api.patch(`/api/v1/emails/${emailId}/mark-unread`);
};

export const getInbox = async (
  page: number = 1,
  perPage: number = 50,
  filters?: EmailFilters,
  emailAccountId?: string | null,
  folder?: string
): Promise<EmailListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
  });

  if (filters?.is_read !== undefined) {
    params.append('is_read', filters.is_read.toString());
  }
  if (filters?.is_archived !== undefined) {
    params.append('is_archived', filters.is_archived.toString());
  }
  if (filters?.is_starred !== undefined) {
    params.append('is_starred', filters.is_starred.toString());
  }
  if (filters?.triage_state) {
    params.append('triage_state', filters.triage_state);
  }
  if (emailAccountId) {
    params.append('email_account_id', emailAccountId);
  }
  if (folder) {
    params.append('folder', folder);
  }

  const response = await api.get<EmailListResponse>(`/api/v1/emails?${params.toString()}`);
  return response.data;
};

export const getEmail = async (id: string): Promise<Email> => {
  const response = await api.get<Email>(`/api/v1/emails/${id}`);
  return response.data;
};

export interface EmailThreadResponse {
  emails: Email[];
  thread_id: string | null;
  count: number;
}

export const getEmailThread = async (id: string): Promise<EmailThreadResponse> => {
  const response = await api.get<EmailThreadResponse>(`/api/v1/emails/${id}/thread`);
  return response.data;
};

export interface LinkScanResult {
  url: string;
  threat_type: 'MALWARE' | 'SOCIAL_ENGINEERING' | 'UNWANTED_SOFTWARE' | 'POTENTIALLY_HARMFUL_APPLICATION';
  metadata?: Record<string, unknown>;
  cache_duration?: string;
}

export interface LinkScanResponse {
  scanned_links: LinkScanResult[];
  has_malicious_links: boolean;
  total_links: number;
  scanned_at?: string;
  cached?: boolean;
  error?: string;
}

export const scanEmailLinks = async (id: string): Promise<LinkScanResponse> => {
  const response = await api.get<LinkScanResponse>(`/api/v1/emails/${id}/link-scan`);
  return response.data;
};

export const sendEmail = async (data: SendEmailRequest): Promise<Email> => {
  const response = await api.post<Email>('/api/v1/emails', data);
  return response.data;
};

export const replyToEmail = async (
  id: string,
  body: string,
  options?: { reply_all?: boolean; to?: string[]; cc?: string[]; bcc?: string[] }
): Promise<Email> => {
  const response = await api.post<Email>(`/api/v1/emails/${id}/reply`, {
    body,
    ...options,
  });
  return response.data;
};

export type ArchiveScope = 'message' | 'thread';

export const archiveEmail = async (id: string, scope?: ArchiveScope): Promise<void> => {
  const query = scope ? `?scope=${scope}` : '';
  await api.post(`/api/v1/emails/${id}/archive${query}`);
};

export const unarchiveEmail = async (id: string): Promise<void> => {
  await api.post(`/api/v1/emails/${id}/unarchive`);
};

export const deleteEmail = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/emails/${id}`);
};

export const searchEmails = async (query: string): Promise<Email[]> => {
  const response = await api.get<{ emails: Email[] }>(`/api/v1/emails/search?q=${encodeURIComponent(query)}`);

  // Handle both array and object responses for backward compatibility
  if (Array.isArray(response.data)) {
    return response.data;
  }

  return response.data.emails || [];
};

export const getEmailFolders = async (emailAccountId: string): Promise<EmailFolder[]> => {
  const response = await api.get<{ folders: EmailFolder[] }>(`/api/v1/emails/folders?email_account_id=${emailAccountId}`);
  return response.data.folders;
};

// OAuth connection methods
export interface OAuthResponse {
  authorization_url: string;
}

export const connectGoogleAccount = (): void => {
  // Get current user from localStorage
  const userStr = localStorage.getItem('concerto_user');
  let userId = '';

  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      userId = user.id;
    } catch (e) {
      console.error('Failed to parse user from localStorage', e);
    }
  }

  // Google OAuth endpoint is a GET that redirects directly
  // Redirect to backend Google OAuth endpoint with add_account flag and user_id
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  const params = new URLSearchParams({
    add_account: 'true',
    user_id: userId
  });
  window.location.href = `${baseUrl}/api/v1/auth/google?${params.toString()}`;
};

export const connectMicrosoftAccount = async (): Promise<void> => {
  // Get current user from localStorage
  const userStr = localStorage.getItem('concerto_user');
  let userId = '';

  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      userId = user.id;
    } catch (e) {
      console.error('Failed to parse user from localStorage', e);
    }
  }

  // Microsoft OAuth endpoint returns authorization URL
  const response = await api.post<OAuthResponse>('/api/v1/auth/microsoft', {
    add_account: 'true',
    user_id: userId
  });
  // Redirect to Microsoft OAuth
  window.location.href = response.data.authorization_url;
};

export const disconnectEmailAccount = async (accountId: string): Promise<void> => {
  await api.delete(`/api/v1/email-accounts/${accountId}`);
};

export interface UpdateEmailAccountRequest {
  nickname?: string | null;
  display_order?: number;
  account_status?: 'archived' | 'active';
}

export const updateEmailAccount = async (accountId: string, data: UpdateEmailAccountRequest): Promise<EmailAccount> => {
  const response = await api.patch<EmailAccount>(`/api/v1/email-accounts/${accountId}`, data);
  return response.data;
};

export const toggleAutoHarmonize = async (accountId: string, enabled: boolean): Promise<{ auto_harmonize_enabled: boolean }> => {
  const response = await api.patch(`/api/v1/email-accounts/${accountId}/auto-harmonize`, { enabled });
  return response.data;
};

export const harmonizeAllEmails = async (accountId: string): Promise<{ analyzed_count: number; error_count: number }> => {
  const response = await api.post(`/api/v1/email-accounts/${accountId}/harmonize-now`);
  return response.data;
};

export const updateSignature = async (accountId: string, signature: string | null): Promise<{ message: string; signature: string | null }> => {
  const response = await api.patch(`/api/v1/email-accounts/${accountId}/signature`, { signature });
  return response.data;
};

export const suggestSignature = async (accountId: string): Promise<{ signatures: string[]; message: string }> => {
  const response = await api.post(`/api/v1/email-accounts/${accountId}/signature/suggest`);
  return response.data;
};

export interface ResyncResult {
  message: string;
  total_fetched: number;
  new_emails: number;
  updated_emails: number;
  contacts_created: number;
  contacts_updated: number;
}

export interface ResyncJobResponse {
  message: string;
  job_id: string;
  status: string;
}

export interface ResyncProgress {
  processed: number;
  total: number;
  new: number;
  updated: number;
  queued_at?: string;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  cancelled_at?: string;
  error?: string;
}

export interface ResyncStatusResponse {
  status: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: ResyncProgress;
}

export const resyncEmailAccount = async (
  accountId: string,
  daysBack: number,
  forceFull: boolean = false
): Promise<ResyncJobResponse> => {
  const response = await api.post<ResyncJobResponse>(`/api/v1/email-accounts/${accountId}/resync`, {
    days_back: daysBack,
    force_full: forceFull
  });
  return response.data;
};

export const getResyncStatus = async (accountId: string): Promise<ResyncStatusResponse> => {
  const response = await api.get<ResyncStatusResponse>(`/api/v1/email-accounts/${accountId}/resync/status`);
  return response.data;
};

export const cancelResync = async (accountId: string): Promise<void> => {
  await api.delete(`/api/v1/email-accounts/${accountId}/resync`);
};

export interface ArchiveBySenderResponse {
  archived_count: number;
  failed_count: number;
  errors?: string[];
}

export const archiveEmailsBySender = async (
  senderEmail: string,
  emailAccountId?: string
): Promise<ArchiveBySenderResponse> => {
  const response = await api.post<ArchiveBySenderResponse>('/api/v1/emails/archive-by-sender', {
    sender_email: senderEmail,
    email_account_id: emailAccountId,
  });
  return response.data;
};

/** MIME types the browser can render inline in a new tab */
const VIEWABLE_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain', 'text/html', 'text/csv', 'text/xml',
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp',
  'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm',
  'video/mp4', 'video/webm', 'video/ogg',
]);

/** Returns true if the browser can natively render this MIME type inline */
export const isViewableMimeType = (mimeType: string): boolean => {
  return VIEWABLE_MIME_TYPES.has(mimeType);
};

/**
 * View an email attachment in a new browser tab (PDF, images, text, etc.)
 * @param attachmentId - The ID of the attachment to view
 * @param mimeType - The MIME type (used to set blob type for correct rendering)
 */
export const viewAttachment = async (attachmentId: string, mimeType: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/attachments/${attachmentId}/download`, {
    credentials: 'include', // Send httpOnly auth cookies
  });

  if (!response.ok) {
    throw new Error('Failed to load attachment');
  }

  const blob = await response.blob();
  const typedBlob = new Blob([blob], { type: mimeType });
  const url = window.URL.createObjectURL(typedBlob);
  window.open(url, '_blank');
  // Revoke after a short delay to allow the new tab to load
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

/**
 * Download an email attachment
 * @param attachmentId - The ID of the attachment to download
 * @param filename - The filename to save the attachment as
 */
export const downloadAttachment = async (attachmentId: string, filename: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/attachments/${attachmentId}/download`, {
    credentials: 'include', // Send httpOnly auth cookies
  });

  if (!response.ok) {
    throw new Error('Failed to download attachment');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// ============ ICS / Calendar Invite ============

export interface ICSInvite {
  summary: string;
  start_time: string | null;
  end_time: string | null;
  location?: string;
  description?: string;
  organizer_email?: string;
  organizer_name?: string;
  attendees: Array<{ email: string; name?: string; status?: string }>;
  uid?: string;
  status?: string;
  method?: string;
}

export interface ParseICSResponse {
  invite: ICSInvite;
  matching_event_id: string | null;
}

export const parseICSAttachment = async (attachmentId: string): Promise<ParseICSResponse> => {
  const response = await api.get<ParseICSResponse>(`/api/v1/attachments/${attachmentId}/parse-ics`);
  return response.data;
};

export const rsvpCalendarEvent = async (
  eventId: string,
  response: 'accepted' | 'declined' | 'tentative'
): Promise<{ message: string; response: string }> => {
  const res = await api.post<{ message: string; response: string }>(
    `/api/v1/attachments/calendar-events/${eventId}/rsvp`,
    { response }
  );
  return res.data;
};

/**
 * Get email counts grouped by sender
 * Returns a map of sender_email -> total count in database
 */
export const getSenderCounts = async (
  emailAccountId?: string | null,
  folder?: string
): Promise<Record<string, number>> => {
  const params = new URLSearchParams();

  if (emailAccountId) {
    params.append('email_account_id', emailAccountId);
  }
  if (folder) {
    params.append('folder', folder);
  }

  const queryString = params.toString();
  const url = queryString ? `/api/v1/emails/sender-counts?${queryString}` : '/api/v1/emails/sender-counts';

  const response = await api.get<Record<string, number>>(url);
  return response.data;
};


export interface SaveAttachmentToDocumentsRequest {
  folder_id?: string | null;
}

export interface SaveAttachmentToDocumentsResponse {
  id: string;
  name: string;
  folder_id: string | null;
  mime_type: string;
  size_bytes: number;
  upload_status: string;
  created_at: string;
}

/**
 * Save an email attachment to the user's document storage
 * @param attachmentId - The ID of the attachment to save
 * @param folderId - Optional folder ID to save to (null for root/unsorted)
 */
export const saveAttachmentToDocuments = async (
  attachmentId: string,
  folderId?: string | null
): Promise<SaveAttachmentToDocumentsResponse> => {
  const response = await api.post<SaveAttachmentToDocumentsResponse>(
    `/api/v1/attachments/${attachmentId}/save-to-documents`,
    { folder_id: folderId }
  );
  return response.data;
};

// Bulk action types
/**
 * Error entries from the bulk endpoints are heterogeneous objects
 * (bulk_email_service.py): per-message provider errors carry `message_id`,
 * batch failures carry `message_ids`, account-level failures carry
 * `email_ids`. All carry an `error` string.
 */
export interface BulkErrorEntry {
  error?: string;
  status_code?: number | null;
  message_id?: string;
  message_ids?: string[];
  email_ids?: string[];
  account_id?: string;
}

export interface BulkActionResponse {
  success_count: number;
  failed_count: number;
  errors?: Array<string | BulkErrorEntry>;
}

/**
 * Bulk set/clear the Betabox triage marker (DB-only, no provider calls)
 * @param emailIds - Email IDs to mark
 * @param state - 'follow_up' | 'task' | null (null clears the marker)
 */
export const bulkTriageEmails = async (
  emailIds: string[],
  state: 'follow_up' | 'task' | null
): Promise<BulkActionResponse> => {
  const response = await api.post<BulkActionResponse>('/api/v1/emails/bulk-triage', {
    email_ids: emailIds,
    state,
  });
  return response.data;
};

/**
 * Bulk archive emails
 * @param emailIds - Array of email IDs to archive
 */
export const bulkArchiveEmails = async (emailIds: string[]): Promise<BulkActionResponse> => {
  const response = await api.post<BulkActionResponse>('/api/v1/emails/bulk-archive', {
    email_ids: emailIds,
  });
  return response.data;
};

/**
 * Bulk mark emails as read
 * @param emailIds - Array of email IDs to mark as read
 */
export const bulkMarkAsRead = async (emailIds: string[]): Promise<BulkActionResponse> => {
  const response = await api.post<BulkActionResponse>('/api/v1/emails/bulk-mark-read', {
    email_ids: emailIds,
  });
  return response.data;
};

/**
 * Bulk mark emails as unread
 * @param emailIds - Array of email IDs to mark as unread
 */
export const bulkMarkAsUnread = async (emailIds: string[]): Promise<BulkActionResponse> => {
  const response = await api.post<BulkActionResponse>('/api/v1/emails/bulk-mark-unread', {
    email_ids: emailIds,
  });
  return response.data;
};

/**
 * Bulk delete emails
 * @param emailIds - Array of email IDs to delete
 */
export const bulkDeleteEmails = async (emailIds: string[]): Promise<BulkActionResponse> => {
  const response = await api.post<BulkActionResponse>('/api/v1/emails/bulk-delete', {
    email_ids: emailIds,
  });
  return response.data;
};

/**
 * Optimistic update wrappers for email actions
 * These provide instant UI feedback with automatic error handling
 */

/**
 * Archive email with optimistic update
 *
 * @param emailId - Email ID to archive
 * @param onOptimisticUpdate - Callback to update local state (remove from list)
 * @param onRevert - Callback to revert local state (restore to list)
 * @param scope - 'message' archives only this email; 'thread' (default on the
 *                server) archives the whole conversation. Pass 'message' from
 *                non-threaded views (date/sender grouping).
 * @returns Promise that resolves when API call completes
 */
export const archiveEmailOptimistic = async (
  emailId: string,
  onOptimisticUpdate: () => void,
  onRevert: () => void,
  scope?: ArchiveScope
): Promise<void> => {
  await performOptimisticUpdate({
    optimisticUpdate: onOptimisticUpdate,
    revertUpdate: onRevert,
    apiCall: () => archiveEmail(emailId, scope),
    successMessage: 'Email archived',
    errorMessage: 'Failed to archive email',
  });
};

/**
 * Delete email with optimistic update
 *
 * @param emailId - Email ID to delete
 * @param onOptimisticUpdate - Callback to update local state (remove from list)
 * @param onRevert - Callback to revert local state (restore to list)
 * @returns Promise that resolves when API call completes
 */
export const deleteEmailOptimistic = async (
  emailId: string,
  onOptimisticUpdate: () => void,
  onRevert: () => void
): Promise<void> => {
  await performOptimisticUpdate({
    optimisticUpdate: onOptimisticUpdate,
    revertUpdate: onRevert,
    apiCall: () => deleteEmail(emailId),
    successMessage: 'Email deleted',
    errorMessage: 'Failed to delete email',
  });
};

/**
 * Mark email as read with optimistic update
 *
 * @param emailId - Email ID to mark as read
 * @param onOptimisticUpdate - Callback to update local state
 * @param onRevert - Callback to revert local state
 * @returns Promise that resolves when API call completes
 */
export const markEmailAsReadOptimistic = async (
  emailId: string,
  onOptimisticUpdate: () => void,
  onRevert: () => void
): Promise<void> => {
  await performOptimisticUpdate({
    optimisticUpdate: onOptimisticUpdate,
    revertUpdate: onRevert,
    apiCall: () => markEmailAsRead(emailId),
    successMessage: 'Marked as read',
    errorMessage: 'Failed to mark as read',
  });
};

/**
 * Mark email as unread with optimistic update
 *
 * @param emailId - Email ID to mark as unread
 * @param onOptimisticUpdate - Callback to update local state
 * @param onRevert - Callback to revert local state
 * @returns Promise that resolves when API call completes
 */
export const markEmailAsUnreadOptimistic = async (
  emailId: string,
  onOptimisticUpdate: () => void,
  onRevert: () => void
): Promise<void> => {
  await performOptimisticUpdate({
    optimisticUpdate: onOptimisticUpdate,
    revertUpdate: onRevert,
    apiCall: () => markEmailAsUnread(emailId),
    successMessage: 'Marked as unread',
    errorMessage: 'Failed to mark as unread',
  });
};
